import { useRef, useState, useEffect, useCallback, type ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface TruncatedTooltipProps {
  children: ReactNode;
  className?: string;
}

export const TruncatedTooltip = ({ children, className }: TruncatedTooltipProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [open, setOpen] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const overflowing = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
    setIsTruncated(overflowing);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation, children]);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [open]);

  if (!isTruncated) {
    return (
      <span ref={ref} className={className}>
        {children}
      </span>
    );
  }

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <span
          ref={ref}
          className={className}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="whitespace-pre-line break-all">{children}</p>
      </TooltipContent>
    </Tooltip>
  );
};
